import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import _ from "lodash";
import {
  Container,
  Flex,
  Avatar,
  Image,
  Box,
  InputWithLabel,
  Heading,
  Message,
  Dropdown,
  Text,
  Button,
  List,
  Input,
  DatePicker
} from "samba-ui";
import moment from "moment";
import styled from "styled-components";
import {
  loadAdminDashboard,
  updateFilter,
  addNewFilter,
  deleteFilter,
  deleteDashboard,
  updateDashboard,
  getDashboardBrandings
} from "../api";
import { loadAdminUsers } from "../redux/users";
import { loadGroupPermissions } from "../redux/groupPermissions";
import Permissions from "./Permissions";
import PageLoader from "./PageLoader";
import DeleteDashboardModal from "./DeleteDashboardModal";
import { DASHBOARD_DROPDOWN_OPTIONS } from "../constants";
import NotifyUsersEmailApproval from "./NotifyUsersEmailApproval";

const infoMessage = "You are not allowed to perform this operation";

const messageCopies = {
  add: {
    success: "New Filter was successfully added",
    info: infoMessage,
    alert: "Error happened while adding new filter"
  },
  update: {
    success: "Filter was successfully updated",
    info: infoMessage,
    alert: "Error happened while updating the filter"
  },
  delete: {
    success: "The filter was successfully deleted",
    info: infoMessage,
    alert: "Error happened while deleting the filter"
  },
  edit: {
    success: "Dashboard information was successfully updated",
    info: infoMessage,
    error: "Error happened while updating the dashboard information"
  },
  deleteDashboard: {
    success: "Dashboard was successfully deleted",
    info: infoMessage,
    error: "Error happened while deleting the dashboard"
  }
};

const DisabledBox = styled(Box)`
  opacity: 0.5;
  pointer-events: none;
`;

const StyledBoxWrapper = styled(Box)`
  width: 230px;
`;

const StyledInputWithLabel = styled(InputWithLabel)`
  width: 100%;
`;

const SingleDashboard = ({
  match,
  user,
  users,
  loadAdminUsers,
  permissions,
  loadGroupPermissions
}) => {
  let history = useHistory();
  const isSuperOperator = user.role === "superoperator";
  const [dashboard, setDashboard] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedGroupPermissions, setSelectedGroupPermissions] = useState([]);
  const [salesforceId, setSalesforceId] = useState("");

  const groupPermissionsDropdownOptions = Object.keys(permissions).map(id => ({
    label: permissions[id].name,
    value: id
  }));

  const [creatorSearchValue, setCreatorSearchValue] = useState("");

  useEffect(() => {
    const req = async () => {
      const res = await getDashboardBrandings();
      const { data } = await res.json();
      setDashboardBrandingOptions(
        data.map(d => ({ label: d.name, value: d.id }))
      );
    };
    req();
  }, []);

  const debounce = useCallback(
    _.debounce(async () => {
      if (!creatorSearchValue) {
        return;
      }
      // setLoading(true);
      const query = `name:${creatorSearchValue}*`;
      await loadAdminUsers(0, query);
      // setLoading(false);
    }, 500),
    [creatorSearchValue]
  );

  useEffect(() => {
    debounce();

    return debounce.cancel;
  }, [debounce]);

  useEffect(() => {
    const req = async () => {
      await loadGroupPermissions();
    };
    req();
  }, [loadGroupPermissions]);

  const onUserSelect = user => {
    setCreatorSearchValue("");
    setSelectedCreator(user.name);
  };

  const setDashboardInformation = useCallback(dashboard => {
    setDashboard(dashboard);
    setName(dashboard.name);
    setPeriscopeId(dashboard.periscopeId);
    setFilters(dashboard.filters);
    setType(
      DASHBOARD_DROPDOWN_OPTIONS.find(option => option.value === dashboard.type)
    );
    if (dashboard.dashboardBrandId) {
      setSelectedBranding({
        label: dashboard.brandName,
        value: dashboard.dashboardBrandId
      });
    }
    setSelectedGroupPermissions(
      dashboard.groupPermissions.map(gp => ({ label: gp.name, value: gp.id }))
    );
    setCreator(dashboard.creator);
    setSalesforceId(dashboard.salesforceId);
    // campaign name id end date not relelavnt for oem like dashboards
    // show conditionally (render)
    if (dashboard.type === "oem") {
      return;
    }
    setCampaignName({
      label: dashboard.campaignName,
      value: dashboard.campaignName
    });
    const campaignEndDate = moment(dashboard.endDate, "YYYY-MM-DD");
    setSelectedEndDate(campaignEndDate);

    ///trf settings
    const trfStartDate = moment(dashboard.trfCampaign.startDate, "YYYYMMDD");
    const trfEndDate = moment(dashboard.trfCampaign.endDate, "YYYYMMDD");
    setStartDate(trfStartDate);
    setEndDate(trfEndDate);
    setCampaignId({
      label: dashboard.campaignTableId,
      value: dashboard.campaignTableId
    });
    setCampaignIdOptions(
      dashboard.trfCampaign.availableIds.map(id => ({
        label: `${id}`,
        value: id
      }))
    );
  }, []);

  useEffect(() => {
    setLoading(true);
    const req = async () => {
      // TODO: Error handling
      const res = await loadAdminDashboard(match.params.id);
      if (res.status !== 200) {
        setError("Error occured while fetching the dashboard");
      }
      const { data: dashboard } = await res.json();
      setLoading(false);
      setDashboardInformation(dashboard);
    };
    req();
  }, [match.params.id, setDashboardInformation]);

  const [name, setName] = useState("");
  const [periscopeId, setPeriscopeId] = useState("");
  const [filters, setFilters] = useState([]);
  const [type, setType] = useState(null);
  const [campaignName, setCampaignName] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [campaignIdOptions, setCampaignIdOptions] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const [creator, setCreator] = useState("");
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [selectedBranding, setSelectedBranding] = useState(null);
  const [dashboardBrandingOptions, setDashboardBrandingOptions] = useState([]);

  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);

  const clearMessage = () => {
    setMessage(null);
  };

  const callApiAndSetMessage = async (type, func, param, extra) => {
    // TODO: Handle Loader here
    let res;
    setLoading(true);
    if (!extra) {
      res = await func(param);
    } else {
      res = await func(param, extra);
    }

    if (res.status === 200) {
      const { data } = await res.json();
      setMessage({ type, variant: "success" });
      setTimeout(clearMessage, 3000);
      setLoading(false);
      return data;
    } else if (res.status === 401) {
      setMessage({ type, variant: "info" });
      setTimeout(clearMessage, 3000);
    } else {
      setMessage({ type, variant: "alert" });
      setTimeout(clearMessage, 3000);
    }
    setLoading(false);
  };

  const onFilterNew = async body => {
    body = body.map(b => {
      b.dashboardId = dashboard.id;
      return b;
    });

    const data = await callApiAndSetMessage("add", addNewFilter, body);
    if (data) {
      setFilters([...filters, ...data]);
    }
  };

  const onFilterDelete = async id => {
    const data = await callApiAndSetMessage("delete", deleteFilter, id);
    if (data) {
      const newFilters = filters.filter(f => f.id !== parseInt(data));
      setFilters([...newFilters]);
    }
  };

  const onFilterUpdate = async (id, body) => {
    const data = await callApiAndSetMessage("update", updateFilter, id, body);
    if (data) {
      const newFilters = filters.map(f => (f.id === data.id ? data : f));
      setFilters(newFilters);
    }
  };

  const onUpdateClick = () => {
    if (
      JSON.stringify(
        selectedGroupPermissions.map(s => s.value).sort((a, b) => a - b)
      ) !==
      JSON.stringify(
        dashboard.groupPermissions.map(s => s.id).sort((a, b) => a - b)
      )
    ) {
      setIsNotifyModalOpen(true);
    } else {
      onClickSendEmail({});
    }
  };

  const onClickSendEmail = async ({
    notifyUser = false,
    notifyFinance = false
  }) => {
    setLoading(true);
    let body = {
      name,
      periscopeId,
      salesforceId,
      type: type.value,
      groupPermissionIds: selectedGroupPermissions.map(gp => gp.value),
      creator: selectedCreator || creator,
      dashboardBrandId: selectedBranding ? selectedBranding.value : null,
      notifyUser,
      notifyFinance
    };

    if (type.value !== "oem") {
      body = {
        ...body,
        endDate: selectedEndDate.format("YYYY-MM-DD"),
        campaignTableId: campaignId.value
      };
    }

    const data = await callApiAndSetMessage(
      "edit",
      updateDashboard,
      dashboard.id,
      body
    );
    if (data) {
      setDashboardInformation(data);
    }
    setLoading(false);
    setIsNotifyModalOpen(false);
  };

  const compareGroupPermissions = () => {
    if (dashboard.groupPermissions.length !== selectedGroupPermissions.length) {
      return true;
    }
    const dgp = dashboard.groupPermissions.map(gp => gp.id);
    const ddgp = selectedGroupPermissions.map(({ value }) => parseInt(value));
    return (
      JSON.stringify(_.difference(ddgp, dgp)) !==
      JSON.stringify(_.difference(dgp, ddgp))
    );
  };

  const checkState = () =>
    name !== dashboard.name ||
    periscopeId !== dashboard.periscopeId ||
    type.value !== dashboard.type ||
    creator !== dashboard.creator ||
    selectedCreator ||
    (selectedBranding &&
      selectedBranding.value !== dashboard.dashboardBrandId) ||
    salesforceId !== dashboard.salesforceId ||
    compareGroupPermissions();

  const checkUpdateButtonState = () => {
    if (nameError) {
      return false;
    }
    if (dashboard && type) {
      if (type.value !== "oem" && campaignId) {
        return (
          checkState() ||
          campaignId.value !== dashboard.campaignTableId ||
          !moment(dashboard.endDate, "YYYY-MM-DD").isSame(
            moment(selectedEndDate)
          )
        );
      }
      return checkState();
    }
  };

  if (loading) {
    return <PageLoader />;
  } else if (error) {
    return (
      <Message variant="alert">Error occured while fetching dashboard</Message>
    );
  }

  const onCampaignEndDateSelected = selected => {
    setSelectedEndDate(selected);
  };

  const onNameChange = e => {
    const { value } = e.target;
    const regex = /^[a-zA-Z0-9\w _-]+$/;
    setName(value);
    if (value.match(regex)) {
      setNameError(null);
    } else {
      setNameError(
        "Enter alpanumeric values and the following special characters _, -, and space "
      );
    }
  };

  const onDeleteSubmit = async () => {
    setLoading(true);
    const res = await deleteDashboard(dashboard.id);
    setLoading(false);
    if (res.status === 200) {
      // redirect to /admin/dashboards
      setMessage({ type: "deleteDashboard", variant: "success" });
      setTimeout(() => {
        history.push("/admin/dashboards");
      }, 1000);
    } else {
      setMessage({ type: "deleteDashboard", variant: "alert" });
    }
  };

  return (
    <Container maxWidth={2048}>
      <Heading color="secondary" fontSize={3} fontWeight={500}>
        Manage Dashboard
      </Heading>
      <Flex>
        <Box width={1 / 4} minWidth={375} my={3}>
          {dashboard && dashboard.banner && (
            <Box>
              <Box>
                <Text fontSize={0} fontWeight={500}>
                  Banner
                </Text>
              </Box>
              <Image
                height={"60px !important"}
                border="1px solid #ececec"
                p={14}
                src={dashboard.banner}
              />
            </Box>
          )}
          <Flex>
            <Avatar
              src={dashboard.logo}
              mr={3}
              mt={3}
              width={80}
              height={80}
              backgroundColor="white"
            />
            <StyledBoxWrapper width={230}>
              <StyledInputWithLabel
                style={{ width: "100%" }}
                onChange={onNameChange}
                value={name}
                labelTitle="Name"
              />
              {nameError && (
                <Box>
                  <Text color="red" fontSize={0} fontWeight={400}>
                    {nameError}
                  </Text>
                </Box>
              )}
              <Box mt={2}>
                <StyledInputWithLabel
                  onChange={e => setPeriscopeId(e.target.value)}
                  value={periscopeId}
                  labelTitle="Dashboard id"
                />
              </Box>
              <Box mt={2}>
                <Box mb={2}>
                  <Text fontSize={0} fontWeight={500}>
                    Type
                  </Text>
                </Box>
                <Dropdown
                  options={DASHBOARD_DROPDOWN_OPTIONS}
                  selected={type}
                  onSelect={setType}
                />
              </Box>
              {dashboard.type !== "oem" && (
                <Box>
                  <Box mt={2}>
                    <Box mb={2}>
                      <Text fontSize={0} fontWeight={500}>
                        Campaign name
                      </Text>
                    </Box>
                    <DisabledBox>
                      <Dropdown selected={campaignName} />
                    </DisabledBox>
                  </Box>
                  <Box mt={2}>
                    <Box mb={2}>
                      <Text fontSize={0} fontWeight={500}>
                        Campaign id
                      </Text>
                    </Box>
                    <Dropdown
                      selected={campaignId}
                      options={campaignIdOptions}
                      onSelect={setCampaignId}
                    />
                  </Box>
                  <Box mt={2}>
                    <Box mb={2}>
                      <Text fontSize={0} fontWeight={500}>
                        Campaign End date
                      </Text>
                    </Box>
                    <DatePicker
                      selectedDate={selectedEndDate}
                      minDate={
                        startDate ? moment(startDate).add(1, "day") : null
                      }
                      maxDate={endDate}
                      onChooseDate={onCampaignEndDateSelected}
                    />
                  </Box>
                </Box>
              )}
              <Box mt={2}>
                <Box mb={2}>
                  <Text fontSize={0} fontWeight={500}>
                    Group Permissions
                  </Text>
                </Box>
                <Dropdown
                  isMulti
                  selected={selectedGroupPermissions}
                  options={groupPermissionsDropdownOptions}
                  onSelect={selected =>
                    setSelectedGroupPermissions(selected || [])
                  }
                />
              </Box>
              <Box mt={2}>
                <Box mb={2}>
                  <Text fontSize={0} fontWeight={500}>
                    Set Creator
                  </Text>
                </Box>
                <Box mb={2}>
                  {creator !== "Unknown" ? (
                    <Input
                      style={{
                        width: "100%",
                        opacity: 0.5,
                        pointerEvents: "none"
                      }}
                      value={creator}
                    />
                  ) : (
                    <Box style={{ position: "relative" }}>
                      <Input
                        style={{ width: "100%" }}
                        value={selectedCreator || creatorSearchValue}
                        onChange={e => {
                          if (!e.target.value) {
                            setSelectedCreator(null);
                          }
                          setCreatorSearchValue(e.target.value);
                        }}
                      />
                      <Box
                        style={{
                          maxHeight: 350,
                          overflow: "auto",
                          position: "absolute",
                          top: 40,
                          width: "100%",
                          zIndex: 2
                        }}
                      >
                        {loading ? (
                          <List.Loading style={{ background: "#fff" }} />
                        ) : creatorSearchValue && Object.keys(users).length ? (
                          Object.keys(users).map(id => {
                            const user = users[id];
                            return (
                              <List.Item
                                key={user.userId}
                                onClick={() => onUserSelect(user)}
                                style={{
                                  width: "100%",
                                  background: "#fff",
                                  borderColor: "#ececec"
                                }}
                              >
                                <SingleUser user={user} />
                              </List.Item>
                            );
                          })
                        ) : creatorSearchValue && !Object.keys(users).length ? (
                          <List.Empty style={{ background: "#fff" }}>
                            No Result Found!
                          </List.Empty>
                        ) : (
                          <></>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box mt={2}>
                <Box mb={2}>
                  <Text fontSize={0} fontWeight={500}>
                    Set Branding
                  </Text>
                </Box>
                <Dropdown
                  isClearable
                  selected={selectedBranding}
                  options={dashboardBrandingOptions}
                  onSelect={setSelectedBranding}
                />
              </Box>
              <Box mt={2}>
                <Box mb={2}>
                  <Text fontSize={0} fontWeight={500}>
                    Set Salesforce Id
                  </Text>
                </Box>
                <Input
                  style={{ width: "100%" }}
                  value={salesforceId}
                  onChange={e => setSalesforceId(e.target.value)}
                />
              </Box>
              <Flex mt={4} alignItems="center" justifyContent="space-between">
                <Button
                  onClick={() => setIsDeleteModalOpen(true)}
                  disabled={isSuperOperator}
                  variant="destructive"
                >
                  Delete
                </Button>

                <Button
                  onClick={onUpdateClick}
                  disabled={isSuperOperator || !checkUpdateButtonState()}
                >
                  Update
                </Button>
              </Flex>
            </StyledBoxWrapper>
          </Flex>
        </Box>
        <Box mt={3} width={3 / 4} minWidth={960}>
          <Heading color="secondary" fontSize={2} fontWeight={500} mb={2}>
            Dashboard Permissions
          </Heading>
          {message && (
            <Message my={2} variant={message.variant}>
              {messageCopies[message.type][message.variant]}
            </Message>
          )}
          <Permissions
            user={user}
            filters={filters}
            onFilterNew={onFilterNew}
            onFilterDelete={onFilterDelete}
            onFilterUpdate={onFilterUpdate}
            dashboardType={dashboard.type}
          />
        </Box>
      </Flex>
      <DeleteDashboardModal
        dashboardName={dashboard.name}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onSubmit={onDeleteSubmit}
        users={dashboard.users || []}
      />
      <NotifyUsersEmailApproval
        isOpen={isNotifyModalOpen}
        onClose={() => setIsNotifyModalOpen(false)}
        onSubmit={onClickSendEmail}
      />
    </Container>
  );
};

SingleDashboard.propTypes = {
  match: PropTypes.object,
  user: PropTypes.object,
  permissions: PropTypes.object,
  loadGroupPermissions: PropTypes.func
};

const mapStateToProps = (state, props) => ({
  users: state.users,
  permissions: state.groupPermissions
});

const mapDispatchToProps = dispatch => ({
  loadGroupPermissions: () => dispatch(loadGroupPermissions()),
  loadAdminUsers: (page, query) => dispatch(loadAdminUsers(page, query))
});

export default connect(mapStateToProps, mapDispatchToProps)(SingleDashboard);

const SingleUser = ({ user, style, clearable, onClear, ...props }) => {
  return (
    <Flex alignItems="center" key={user.userId} style={style} {...props}>
      <Box mr={2}>
        <Image
          borderRadius={4}
          src={user.imageURL}
          width={"40px !important"}
          height={"40px !important"}
        />
      </Box>
      <Box>
        <Text fontSize={1} color="palette.ink.normal">
          {user.name}
        </Text>
      </Box>
      <Box style={{ marginLeft: "auto" }}>
        {clearable ? (
          <Button variant="destructive" onClick={onClear}>
            X
          </Button>
        ) : (
          <Button>+</Button>
        )}
      </Box>
    </Flex>
  );
};

SingleUser.propTypes = { user: PropTypes.object };
